import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchCart as apiFetchCart,
  addToCart as apiAddToCart,
  updateCartItem as apiUpdateCartItem,
  removeCartItem as apiRemoveCartItem,
  clearCart as apiClearCart,
  mergeCart as apiMergeCart,
  updateCartVariant as apiUpdateCartVariant,
} from "../services/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const mergeInFlightRef = useRef(false);
  const lastAuthRef = useRef(isAuthenticated);

  const GUEST_CART_KEY = "guest_cart";

  const getItemKey = (item) => {
    if (item?.id) return `ci-${item.id}`;
    const pid = item?.productId ?? item?.id ?? "unknown";
    const vid = item?.variantId ?? "base";
    return `g-${pid}-${vid}`;
  };

  const normalizeApiCart = useCallback((payload) => {
    const data = payload?.data || payload || {};
    const items = Array.isArray(data.items) ? data.items : [];

    return items.map((item) => ({
      id: item.id ?? item.cart_item_id ?? null,
      productId: item.productId ?? item.product_id ?? item.id ?? null,
      variantId: item.variantId ?? item.variant_id ?? null,
      businessId: item.businessId ?? item.business_id ?? null,
      quantity: Number(item.quantity || 0),
      price: Number(item.price || 0),
      priceCents: item.priceCents ?? item.price_cents ?? null,
      name: item.name ?? item.product_name ?? "",
      image: item.image ?? item.product_image ?? null,
      businessName: item.businessName ?? item.business_name ?? null,
      variation: item.variation ?? item.variant_title ?? item.variant_sku ?? null,
      stock: item.stock ?? item.available_stock ?? null,
      isSelected: item.isSelected ?? item.is_selected ?? true,
      // Only the server knows; a guest cart has no answer and is not blocked.
      storeAcceptsDeliveryOrders: item.storeAcceptsDeliveryOrders ?? null,
      key: getItemKey(item),
    }));
  }, []);

  const loadGuestCart = useCallback(() => {
    try {
      const raw = localStorage.getItem(GUEST_CART_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.map((item) => ({ ...item, key: getItemKey(item) }));
    } catch {
      return [];
    }
  }, []);

  const saveGuestCart = useCallback((items) => {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
    } catch {}
  }, []);

  const loadCart = useCallback(async () => {
    if (!isAuthenticated) {
      const guestItems = loadGuestCart();
      setCart(guestItems);
      setLoading(false);
      return guestItems;
    }

    setLoading(true);
    try {
      const data = await apiFetchCart();
      const normalized = normalizeApiCart(data);
      setCart(normalized);
      return normalized;
    } catch {
      setCart([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadGuestCart, normalizeApiCart]);

  const mergeGuestCart = useCallback(async () => {
    if (!isAuthenticated || mergeInFlightRef.current) return;
    const guestItems = loadGuestCart();
    if (guestItems.length === 0) return;

    mergeInFlightRef.current = true;
    try {
      const payload = guestItems.map((item) => ({
        productId: item.productId,
        variantId: item.variantId ?? null,
        quantity: item.quantity,
      }));
      await apiMergeCart(payload);
      saveGuestCart([]);
    } catch {
      // leave guest cart intact if merge fails
    } finally {
      mergeInFlightRef.current = false;
    }
  }, [isAuthenticated, loadGuestCart, saveGuestCart]);

  useEffect(() => {
    let mounted = true;

    const sync = async () => {
      if (!mounted) return;
      if (isAuthenticated) {
        if (!lastAuthRef.current) {
          await mergeGuestCart();
        }
        await loadCart();
      } else {
        setCart(loadGuestCart());
        setLoading(false);
      }
      lastAuthRef.current = isAuthenticated;
    };

    sync();
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, loadCart, loadGuestCart, mergeGuestCart]);

  /**
   * Add product to cart with specified quantity.
   * Optimistically updates local state, then syncs with backend.
   * @param {Object} product - Product object with id, name, price, image
   * @param {number} quantity - Number of items to add (default: 1)
   */
  const addItem = async (product, quantity = 1) => {
    const payload = {
      productId: product?.productId ?? product?.id ?? product?._id,
      variantId: product?.variantId ?? product?.variant_id ?? null,
      quantity: Number(quantity || 1),
    };

    if (!payload.productId || payload.quantity <= 0) return;

    if (!isAuthenticated) {
      setCart((prev) => {
        const next = [...prev];
        const key = getItemKey(payload);
        const idx = next.findIndex((item) => item.key === key);
        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            quantity: next[idx].quantity + payload.quantity,
          };
        } else {
          next.push({
            id: null,
            productId: payload.productId,
            variantId: payload.variantId,
            quantity: payload.quantity,
            price: Number(product?.price || 0),
            name: product?.name || "",
            image: product?.image || null,
            businessId: product?.businessId ?? product?.business_id ?? null,
            businessName: product?.businessName ?? product?.business_name ?? null,
            variation: product?.variation ?? null,
            stock: product?.stock ?? null,
            isSelected: true,
            key,
          });
        }
        saveGuestCart(next);
        return next;
      });
      return;
    }

    try {
      const data = await apiAddToCart(payload);
      setCart(normalizeApiCart(data));
    } catch {
      await loadCart();
    }
  };

  const buyNow = async (product, quantity = 1) => {
    await addItem(product, quantity);
  };

  const updateQuantity = async (item, quantity) => {
    if (!item) return;
    const nextQty = Number(quantity);
    if (!Number.isFinite(nextQty)) return;

    if (!isAuthenticated) {
      setCart((prev) => {
        const next = prev
          .map((it) => (it.key === item.key ? { ...it, quantity: nextQty } : it))
          .filter((it) => it.quantity > 0);
        saveGuestCart(next);
        return next;
      });
      return;
    }

    try {
      const data = await apiUpdateCartItem({
        itemId: item.id,
        productId: item.productId,
        variantId: item.variantId,
        quantity: nextQty,
      });
      setCart(normalizeApiCart(data));
    } catch {
      await loadCart();
    }
  };

  const increaseItem = (item) => updateQuantity(item, (item?.quantity || 0) + 1);
  const decreaseItem = (item) => {
    const currentQuantity = Number(item?.quantity || 0);
    if (currentQuantity <= 1) return;
    return updateQuantity(item, currentQuantity - 1);
  };

  const setItemSelected = async (item, selected) => {
    if (!item) return;
    const nextSelected = Boolean(selected);

    if (!isAuthenticated) {
      setCart((prev) => {
        const next = prev.map((it) =>
          it.key === item.key ? { ...it, isSelected: nextSelected } : it
        );
        saveGuestCart(next);
        return next;
      });
      return;
    }

    try {
      const data = await apiUpdateCartItem({
        itemId: item.id,
        productId: item.productId,
        variantId: item.variantId,
        isSelected: nextSelected,
      });
      setCart(normalizeApiCart(data));
    } catch {
      await loadCart();
    }
  };

  const setAllSelected = async (selected) => {
    const nextSelected = Boolean(selected);

    if (!isAuthenticated) {
      setCart((prev) => {
        const next = prev.map((it) => ({ ...it, isSelected: nextSelected }));
        saveGuestCart(next);
        return next;
      });
      return;
    }

    try {
      const data = await apiUpdateCartItem({
        scope: "all",
        isSelected: nextSelected,
      });
      setCart(normalizeApiCart(data));
    } catch {
      await loadCart();
    }
  };

  const setSellerSelected = async (businessId, selected) => {
    const nextSelected = Boolean(selected);
    if (!businessId) return;

    if (!isAuthenticated) {
      setCart((prev) => {
        const next = prev.map((it) =>
          it.businessId === businessId ? { ...it, isSelected: nextSelected } : it
        );
        saveGuestCart(next);
        return next;
      });
      return;
    }

    try {
      const data = await apiUpdateCartItem({
        scope: "business",
        businessId,
        isSelected: nextSelected,
      });
      setCart(normalizeApiCart(data));
    } catch {
      await loadCart();
    }
  };

  const removeItem = async (item) => {
    if (!item) return;

    if (!isAuthenticated) {
      setCart((prev) => {
        const next = prev.filter((it) => it.key !== item.key);
        saveGuestCart(next);
        return next;
      });
      return;
    }

    try {
      const data = await apiRemoveCartItem({
        itemId: item.id,
        productId: item.productId,
        variantId: item.variantId,
      });
      setCart(normalizeApiCart(data));
    } catch {
      await loadCart();
    }
  };

  const clearCart = async () => {
    setCart([]);
    if (!isAuthenticated) {
      saveGuestCart([]);
      return;
    }

    try {
      await apiClearCart();
    } catch {}
  };

  const updateVariant = async (item, newVariantId) => {
    if (!item || !newVariantId || !isAuthenticated) return;

    try {
      const data = await apiUpdateCartVariant({ itemId: item.id, newVariantId });
      setCart(normalizeApiCart(data));
    } catch {
      await loadCart();
    }
  };

  const totals = useMemo(() => {
    const itemCount = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const subtotal = cart.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
    return { itemCount, subtotal };
  }, [cart]);

  const selectedItems = useMemo(() => cart.filter((i) => i.isSelected), [cart]);

  const selectedTotals = useMemo(() => {
    const count = selectedItems.reduce((s, i) => s + (i.quantity || 0), 0);
    const subtotal = selectedItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 0), 0);
    return { itemCount: selectedItems.length, count, subtotal };
  }, [selectedItems]);

  const value = {
    cart,
    loading,
    totals,
    selectedItems,
    selectedTotals,
    cartCount: totals.itemCount,
    addItem,
    buyNow,
    increaseItem,
    decreaseItem,
    updateQuantity,
    setItemSelected,
    setAllSelected,
    setSellerSelected,
    removeItem,
    clearCart,
    updateVariant,
    loadCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export default CartContext;
